import React, { useEffect, useState, useMemo } from 'react';
import { IonRow, IonCol, IonText, IonAlert, IonGrid, IonButton } from '@ionic/react';
import './BasketItemRow.scss';
import { formatCurrency } from '../helpers/currencyHelpers';
import { getConfig } from '../helpers/configHelper';
import * as MenuItemDataStore from '../store/MenuItemStore';
import * as BasketStore from '../store/BasketStore';
import * as DealStore from '../store/DealStore';
import { useHistory } from "react-router-dom";
import { bindActionCreators } from 'redux';
import { useDispatch } from 'react-redux';
import AltQuantityInput from './AltQuantityInput';
import { inlineBrandImage } from '../helpers/menuHelpers';

interface BasketItemRowProps {
    basketItem: IMopMenuItemDetail;
    readOnly: boolean;
    //onClick?: (basketItemId: number) => void;
    isColleague?: boolean;
}

type BasketItemRowMergeProps =
    typeof MenuItemDataStore.actionCreators
    & BasketItemRowProps;

const BasketItemRow: React.FC<BasketItemRowProps> = ({ basketItem, readOnly, isColleague }) => {

    let history = useHistory();
    const boundRequestBasketItem = bindActionCreators(MenuItemDataStore.actionCreators.requestBasketMenuItem, useDispatch());
    const boundUpdateBasketItemQuantity = bindActionCreators(BasketStore.actionCreators.updateBasketItemQuantity, useDispatch());
    const boundStartDeal = bindActionCreators(DealStore.actionCreators.startDeal, useDispatch());
    const [removeAlertIsOpen, setRemoveAlertIsOpen] = useState(false);
    const [totalPrice, setTotalPrice] = useState(basketItem.menuItemTotalPrice);

    // put in back pocket
    const isCoupon = basketItem.sku.split('_').length == 2 && basketItem.isDiscount;

    const getCouponCode = () => {
        return basketItem.sku.split("_")[1];
    }

    const menuItemName = useMemo(() => {
        let menuItemName = "";
        if (basketItem.quantity > 1 && totalPrice < 0) {
            menuItemName += `${basketItem.quantity} x `;
        }

        if (basketItem.productNameBasketOverride && !isColleague) {
            menuItemName += basketItem.productNameBasketOverride;
        } else {
            menuItemName += inlineBrandImage(basketItem.menuItemName, basketItem.hasBrandImage);
        }

        return menuItemName;
    }, [basketItem.menuItemName, basketItem.productNameBasketOverride, basketItem.quantity, basketItem.hasBrandImage, totalPrice, isColleague]);

    useEffect(() => {
        setTotalPrice(basketItem.menuItemTotalPrice);
    }, [basketItem.menuItemTotalPrice])

    return (
        <div className="BasketItemRowOuter Promotion">
            <IonAlert
                isOpen={removeAlertIsOpen}
                onDidDismiss={() => { setRemoveAlertIsOpen(false) }}
                header={"Remove Product"}
                buttons={[
                    {
                        text: "No",
                        role: "cancel"
                    },
                    {
                        text: "Yes, remove it",
                        handler: () => {
                            boundUpdateBasketItemQuantity(basketItem.basketMenuItemId, 0);
                        }
                    }
                ]}
                message={"Are you sure you want to remove this product from your basket?"}
            />
            {basketItem &&
                <IonRow key={basketItem.menuItemId} className="basketItemRow" >
                    <IonCol className="ion-no-padding">
                        <IonGrid>
                            <IonRow className="basket-item-title-and-price-row">
                                <IonCol size="8">
                                    <IonText className="BasketItemTitle">
                                        {isCoupon &&
                                            <span>{getCouponCode()} - </span>
                                        }

                                        <span className='align-children' dangerouslySetInnerHTML={{ __html: menuItemName }} />
                                    </IonText>
                                </IonCol>
                                <IonCol size="4" className="ion-text-end">
                                    <IonText className="BasketItemPrice">
                                        {`${formatCurrency(totalPrice * basketItem.quantity)}${basketItem.quantity > 1 ? ` (${formatCurrency(totalPrice)} each)` : ""}`}
                                    </IonText>
                                </IonCol>
                            </IonRow>
                            <IonRow className="basket-item-description-row">
                                <IonCol size="8">
                                    <ul className="BasketItemDescription">
                                        <>
                                            {basketItem.productNameBasketOverride && !isColleague &&
                                                <p key='productName' className="text-xl">
                                                    <span dangerouslySetInnerHTML={{ __html: basketItem.menuItemName.replace(/\[\[.*\]\] /, '') }} />
                                                </p>
                                            }
                                            {basketItem.optionsTextList?.map((row) => {
                                                const text = row.text.replace(/\[\[.*\]\] /, '').trim();

                                                if (row?.isExtra) {
                                                    return (
                                                        <p key={row.text} className='text-xl extra'>
                                                            <strong>{'+ '}</strong>
                                                            <span dangerouslySetInnerHTML={{ __html: text }}></span>
                                                        </p>
                                                    )
                                                } else {
                                                    return (
                                                        <p key={row.text} className='text-xl'>
                                                            <span></span>
                                                            <span dangerouslySetInnerHTML={{ __html: text }}></span>
                                                        </p>
                                                    )
                                                }
                                            })}
                                        </>
                                    </ul>
                                </IonCol>
                            </IonRow>
                            <IonRow className="basket-item-edit-and-quantity-row">
                                <IonCol size="6">
                                    {(!readOnly && basketItem.editable) && <div className="basketItemEditButton" onClick={() => {
                                        if (basketItem.isDealItem) {
                                            boundStartDeal(basketItem.sku);
                                        }

                                        boundRequestBasketItem(basketItem.basketMenuItemId, () => {
                                            if (basketItem.isDealItem) {
                                                const params = new URLSearchParams();
                                                params.set('page', '1');

                                                history.push(`/deal/${basketItem.menuItemId}?${params.toString()}`);
                                            } else {
                                                history.push(`/menuitem/${basketItem.menuItemId}`);
                                            }
                                        })
                                    }}>Edit</div>}
                                </IonCol>
                                <IonCol size="6" className="basket-item-quantity-col">

                                    {(!readOnly && !basketItem.isDiscount) && <AltQuantityInput
                                        value={basketItem.quantity}
                                        subtractButtonColour={getConfig().isMarket ? 'lightgreen' : 'grey'}
                                        addButtonColour={getConfig().isMarket ? 'lightgreen' : 'grey'}
                                        onChange={(quantity) => {

                                            let proceed = true;
                                            if (quantity < 1) {
                                                proceed = false;
                                                setRemoveAlertIsOpen(true);
                                            }
                                            if (proceed) {
                                                boundUpdateBasketItemQuantity(basketItem.basketMenuItemId, quantity);
                                            }
                                        }}
                                    />}

                                </IonCol>
                            </IonRow>
                        </IonGrid>
                    </IonCol>
                </IonRow>
            }
        </div>
    );
};

export default BasketItemRow;

/*

<IonCol size="4">
    <IonRow>
        <IonCol size="4">
            <div className="ProductOptionButton">-</div>

        </IonCol>
        <IonCol size="4">
            <div className="ProductOptionCount"><div className="ProductOptionNumber">5</div></div>
        </IonCol>
        <IonCol size="4">
            <div className="ProductOptionButton">+</div>
        </IonCol>
    </IonRow>
</IonCol>
*/
